/**
 * Z_TIERS — Mobile Shell Contract §9 z-index hierarchy.
 *
 * Single source of truth for z-index values across the platform.
 * Every overlay primitive (`<MobileMenu>`, `<MobilePopover>`,
 * `<MobileBottomSheet>`, `<MobileDialog>`) and the contract linter
 * read from this map. Bespoke overlays must declare their tier
 * from here too — never invent a new number.
 *
 * Why: future agents (human or AI) cannot accidentally regress
 * the §9 contract because the runtime z-index AND the linter
 * heuristics share one definition.
 *
 * Inserting a new tier?
 *   1. Add it here with a clear comment.
 *   2. Update `/app/memory/MOBILE_SHELL_CONTRACT.md` §9.
 *   3. Add a check in `frontend/scripts/check-mobile-contract.js`
 *      if the new tier must be enforced.
 *
 * Phase 2 Batch 5 (May 14, 2026) — extracted from inlined values
 * in the primitive components.
 */

export const Z_TIERS = Object.freeze({
  /** Base UI chrome (sticky headers, mobile bottom nav). */
  CHROME: 40,

  /** Standard modal dialogs (MobileOverlayBackdrop default priority). */
  MODAL: 50,

  /** Standard menus / dropdowns / selects / popovers. */
  MENU: 60,

  /** Notification feed popover (raised above chrome but below sheets). */
  POPOVER: 60,

  /** Bottom sheets and side-anchored sheets. */
  SHEET: 1100,

  /** Operational continuity Active Operation pill. */
  ACTIVE_OP_PILL: 2000,

  /** Long-press info sheet floating above the Active Op pill. */
  ACTIVE_OP_INFO: 2001,

  /** Modal dialogs with priority="urgent" (sudo gates, payment, NPS). */
  URGENT_MODAL: 9999,

  /** Phase 2 Batch 5 alias: short calm name for the urgent tier.
   * Both `URGENT_MODAL` and `URGENT` resolve to the same value so
   * old call sites stay untouched and new call sites can use the
   * shorter form. */
  URGENT: 9999,

  /** Highest tier — PhoneMountGuidance overlay scope. */
  CRITICAL: 10000,
});

/** Tailwind className helpers — use these in className strings so the
 * z-index is grep-able and changeable from one place. */
export const Z_CLASS = Object.freeze({
  CHROME: "z-40",
  MODAL: "z-50",
  MENU: "z-[60]",
  POPOVER: "z-[60]",
  SHEET: "z-[1100]",
  ACTIVE_OP_PILL: "z-[2000]",
  ACTIVE_OP_INFO: "z-[2001]",
  URGENT_MODAL: "z-[9999]",
  URGENT: "z-[9999]",
  CRITICAL: "z-[10000]",
});

export default Z_TIERS;
